import Type from 'src/Type';
import config from 'src/utils/config';

import Node from '../Node';

import type Context from 'src/Context';
import type { Expression } from 'src/types';

export interface UpdateCleansedDataNodeT {
    $type: 'UpdateCleansedDataNode';
    id: Expression;
    asin: Expression;
    data: Expression;
}

export default class UpdateCleansedDataNode extends Node<UpdateCleansedDataNodeT> {
    static TYPE = Type.Object(
        {
            key: 'id',
            label: 'CleansedData._id',
            type: 'StringNode',
            suggestions: 'string',
        },
        { key: 'asin', label: 'ASIN', type: 'StringNode' },
        { key: 'data', label: 'Data', type: 'ObjectNode' },
    );

    static uiConfig = {
        ...config.presets.tasker,
        description: [
            'Write values to the data points of an existing CleansedData record, with source `ops:tasker`.',
            '',
            "`data` is keyed by data point name (e.g. `name`, `brand`, `nutritionFacts`). Supply an `asin` from the record's identifiers to write values for that ASIN, or leave it empty for record-level values.",
        ].join('\n'),
    };

    static getDefault(): Expression {
        return {
            ...super.getDefault(),
            id: this.getDefaultFor('StringNode'),
            asin: this.getDefaultFor('StringNode'),
            data: this.getDefaultFor('ObjectNode'),
        };
    }

    async execute(context: Context): Promise<void> {
        const id = await context.evaluateLog('id', this.expression.id);
        const asin = await context.evaluateLog('asin', this.expression.asin);
        const data = await context.evaluateLog('data', this.expression.data);

        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('CleansedData values must be an object');
        }

        context.addTaskerAction({
            kind: 'updateCleansedData',
            cleansedData: id,
            asin,
            data,
        });
    }
}
